import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Wifi, 
  WifiOff, 
  Search, 
  X, 
  RefreshCw,
  Signal,
  Settings,
  Play,
  Square,
  Trash2
} from 'lucide-react'

const DeviceDiscovery = ({
  devices,
  isConnecting,
  connectionError,
  onDeviceSelect,
  onQuickScan,
  onClearDevices,
  onRemoveDevice,
  selectedDevice
}) => {
  const [showManualEntry, setShowManualEntry] = useState(false)
  const [manualIP, setManualIP] = useState('')
  const [manualPort, setManualPort] = useState('80')

  const handleManualConnect = async () => {
    if (manualIP.trim()) {
      try {
        console.log('🔧 Attempting manual connection to:', manualIP, 'port:', manualPort)
        
        // Test the connection first using axios (better error handling)
        const response = await fetch(`http://${manualIP}:${manualPort}/status`, {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        })
        
        console.log('Response status:', response.status)
        
        if (response.ok) {
          const deviceData = await response.json()
          console.log('✅ Device data received:', deviceData)
          
          const device = {
            ip: manualIP.trim(),
            port: parseInt(manualPort) || 80,
            name: deviceData.name || `Manual Device (${manualIP})`,
            mode: deviceData.mode || 'manual',
            status: 'connected',
            version: deviceData.version,
            ssid: deviceData.ssid,
            rssi: deviceData.rssi,
            mac: deviceData.mac,
            pinStates: deviceData.pinStates || {}
          }
          
          console.log('🎯 Created device object:', device)
          onDeviceSelect(device)
          setShowManualEntry(false)
          setManualIP('')
        } else {
          console.error('❌ Device not responding properly:', response.status, response.statusText)
          alert(`Device not responding properly (${response.status}). Please check the IP address and make sure the NodeMCU is running.`)
        }
      } catch (error) {
        console.error('❌ Manual connection failed:', error)
        alert(`Connection failed: ${error.message}. Please check the IP address and make sure the NodeMCU is running.`)
      }
    }
  }

  const handleQuickScan = async () => {
    try {
      await onQuickScan()
    } catch (error) {
      console.error('Quick scan failed:', error)
    }
  }

  const handleRemoveDevice = (deviceIP) => {
    if (onRemoveDevice) {
      onRemoveDevice(deviceIP)
    }
  }

  const getSignalStrength = (rssi) => {
    if (rssi >= -50) return { bars: 4, color: 'text-success-400' }
    if (rssi >= -60) return { bars: 3, color: 'text-warning-400' }
    if (rssi >= -70) return { bars: 2, color: 'text-orange-400' }
    return { bars: 1, color: 'text-danger-400' }
  }

  const renderSignalBars = (rssi) => {
    const { bars, color } = getSignalStrength(rssi)
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4].map((bar) => (
          <div
            key={bar}
            className={`w-1 h-${bar} rounded-full ${
              bar <= bars ? color : 'bg-gray-600'
            }`}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Device Discovery</h3>
        <div className="flex items-center space-x-2">
          {isConnecting ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            >
              <RefreshCw className="w-4 h-4 text-primary-400" />
            </motion.div>
          ) : (
            <Search className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Connection Error */}
      {connectionError && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
          <div className="flex items-center space-x-2">
            <X className="w-4 h-4 text-red-400" />
            <span className="text-sm text-red-300">{connectionError}</span>
          </div>
        </div>
      )}

      {/* Scan Controls */}
      <div className="flex items-center space-x-2 mb-4">
        <button
          onClick={handleQuickScan}
          disabled={isConnecting}
          className="btn-primary flex items-center space-x-2 text-sm"
        >
          <Play className="w-4 h-4" />
          <span>{isConnecting ? 'Scanning...' : 'Quick Scan'}</span>
        </button>
        
        <button
          onClick={() => setShowManualEntry(!showManualEntry)}
          className="btn-secondary flex items-center space-x-2 text-sm"
        >
          <Settings className="w-4 h-4" />
          <span>Manual Entry</span>
        </button>
        
        {devices.length > 0 && (
          <button
            onClick={onClearDevices}
            className="btn-danger flex items-center space-x-2 text-sm"
          >
            <Trash2 className="w-4 h-4" />
            <span>Clear All</span>
          </button>
        )}
      </div>

      {/* Manual Entry */}
      <AnimatePresence>
        {showManualEntry && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 p-3 bg-secondary-500/20 rounded-lg"
          >
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">IP Address</label>
                <input
                  type="text"
                  value={manualIP}
                  onChange={(e) => setManualIP(e.target.value)}
                  placeholder="192.168.1.100"
                  className="w-full px-3 py-2 bg-secondary-600 border border-secondary-500 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Port</label>
                <input
                  type="number"
                  value={manualPort}
                  onChange={(e) => setManualPort(e.target.value)}
                  placeholder="80"
                  className="w-full px-3 py-2 bg-secondary-600 border border-secondary-500 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
                />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleManualConnect}
                  disabled={!manualIP.trim()}
                  className="btn-primary flex-1 text-sm"
                >
                  Connect
                </button>
                <button
                  onClick={() => setShowManualEntry(false)}
                  className="btn-secondary text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Device List */}
      <div className="space-y-2">
        {devices.length === 0 ? (
          <div className="text-center py-8">
            {isConnecting ? (
              <div>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-3"
                />
                <p className="text-sm text-gray-400">Scanning for devices...</p>
              </div>
            ) : (
              <div>
                <Search className="w-8 h-8 text-gray-500 mx-auto mb-3" />
                <p className="text-sm text-gray-400">No devices found</p>
                <p className="text-xs text-gray-500 mt-1">
                  Use "Quick Scan" to find devices or "Manual Entry" to connect directly
                </p>
              </div>
            )}
          </div>
        ) : (
          devices.map((device, index) => (
            <motion.div
              key={`${device.ip}-${device.port}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-3 rounded-lg border transition-all duration-200 ${
                selectedDevice?.ip === device.ip
                  ? 'border-primary-500 bg-primary-500/20'
                  : 'border-secondary-500 bg-secondary-500/20 hover:border-primary-500/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div 
                  className="flex-1 cursor-pointer"
                  onClick={() => onDeviceSelect(device)}
                >
                  <div className="flex items-center space-x-2 mb-1">
                    <Wifi className="w-4 h-4 text-primary-400" />
                    <span className="font-medium text-white">
                      {device.name}
                    </span>
                    {selectedDevice?.ip === device.ip && (
                      <span className="text-xs bg-primary-500 text-white px-2 py-1 rounded">
                        Connected
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-400">
                    {device.ip}:{device.port}
                  </div>
                  <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                    <span>Mode: {device.mode}</span>
                    <span>SSID: {device.ssid}</span>
                    {device.version && <span>v{device.version}</span>}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {device.rssi && renderSignalBars(device.rssi)}
                  <div className="text-right">
                    <div className="text-xs text-gray-400">
                      {device.rssi ? `${device.rssi} dBm` : 'Unknown'}
                    </div>
                  </div>
                  {onRemoveDevice && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemoveDevice(device.ip)
                      }}
                      className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Scan Status */}
      {isConnecting && (
        <div className="mt-4 p-3 bg-primary-500/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            >
              <RefreshCw className="w-4 h-4 text-primary-400" />
            </motion.div>
            <span className="text-sm text-primary-300">
              Scanning for EZ Control devices...
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default DeviceDiscovery 